<!-- Main Content -->
<div
  class="w-full max-w-6xl mx-auto flex flex-col gap-4 border border-gray-300 rounded-lg bg-white"
>
  <!-- Header -->
  <lib-feature-header
    [title]="'Commission Settings'"
    (closeClick)="goBack()"
    [isLoading]="store.loading()"
  />

  <!-- Tab Navigation -->
  <div class="animate-slideInUp" style="animation-delay: 100ms">
    <sb-tabs-navigation
      [tabs]="tabs"
      [activeTab]="activeTab()"
      (tabChanged)="onTabChange($event)"
    />
  </div>

  <form [formGroup]="form" class="p-4 flex flex-col gap-4">
    <!-- Payout Tab -->
    @if (activeTab() === 'payout') {
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <shared-select-input
        formControlName="payout_frequency"
        label="Payout Frequency"
        [options]="payoutFrequencyOptions"
        [required]="true"
        [disabled]="store.loading() || !canUpdate()"
        fieldId="payout_frequency"
        placeholder="Select frequency"
      />

      <sb-text-input
        id="payout_threshold_amount"
        formControlName="payout_threshold_amount"
        label="Minimum Payout Amount"
        type="number"
        min="0"
        step="0.01"
        placeholder="0.00"
        [readonly]="store.loading() || !canUpdate()"
        helpText="Hold commissions below this amount until the threshold is met. Set 0 to disable."
      />

      <sb-text-input
        id="default_currency"
        formControlName="default_currency"
        label="Default Currency"
        type="text"
        placeholder="KES"
        [required]="true"
        [readonly]="store.loading() || !canUpdate()"
        helpText="ISO 4217 code (e.g. KES, USD, EUR, GBP)."
      />

      <shared-select-input
        formControlName="fiscal_year_start_month"
        label="Fiscal Year Start Month"
        [options]="fiscalMonthOptions"
        [required]="true"
        [disabled]="store.loading() || !canUpdate()"
        fieldId="fiscal_year_start_month"
        placeholder="Select month"
      />
    </div>
    }

    <!-- Retainer Tab -->
    @if (activeTab() === 'retainer') {
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <sb-text-input
        id="retainer_min_duration_months"
        formControlName="retainer_min_duration_months"
        label="Minimum Retainer Duration (months)"
        type="number"
        min="1"
        max="60"
        step="1"
        placeholder="6"
        [required]="true"
        [readonly]="store.loading() || !canUpdate()"
        helpText="A referral must meet this duration to qualify for retainer commission."
      />
    </div>
    }

    <!-- Notifications Tab -->
    @if (activeTab() === 'notifications') {
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <!-- Notify on Created -->
      <div class="md:col-span-2 rounded-lg border border-gray-200 p-4">
        <label class="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            class="mt-1 h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500 disabled:cursor-not-allowed"
            [checked]="form.get('notify_on_commission_created')?.value"
            [disabled]="store.loading() || !canUpdate()"
            (change)="onNotifyOnCreatedChange($event)"
          />
          <div>
            <div class="text-sm font-medium text-gray-900">
              Notify when commission is created
            </div>
            <p class="mt-1 text-sm text-gray-600">
              Send a notification to the referrer when a new commission is
              calculated on referral closure.
            </p>
          </div>
        </label>
      </div>

      <!-- Notify on Paid -->
      <div class="md:col-span-2 rounded-lg border border-gray-200 p-4">
        <label class="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            class="mt-1 h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500 disabled:cursor-not-allowed"
            [checked]="form.get('notify_on_commission_paid')?.value"
            [disabled]="store.loading() || !canUpdate()"
            (change)="onNotifyOnPaidChange($event)"
          />
          <div>
            <div class="text-sm font-medium text-gray-900">
              Notify when commission is paid
            </div>
            <p class="mt-1 text-sm text-gray-600">
              Send a notification to the referrer when their commission status
              changes to paid.
            </p>
          </div>
        </label>
      </div>

      <sb-text-input
        id="approval_required_above_amount"
        formControlName="approval_required_above_amount"
        label="Approval Required Above Amount"
        type="number"
        min="0"
        step="0.01"
        placeholder="Leave blank to disable"
        [readonly]="store.loading() || !canUpdate()"
        helpText="Commissions above this amount require manager approval before payout."
      />
    </div>
    }
  </form>

  <!-- Error Display -->
  @if (store.error()) {
  <div class="px-4 pb-4">
    <lib-error-alert [error]="store.error()" />
  </div>
  }

  <!-- Form Actions -->
  <div class="px-4 pb-4">
    <div class="flex justify-end space-x-4">
      <shared-submit-button
        [buttonText]="'Save'"
        [disabled]="!form.dirty || store.loading() || !canUpdate()"
        [isLoading]="store.loading()"
        (buttonClick)="onSave()"
      />
      <shared-submit-button
        [buttonText]="'Cancel'"
        [variant]="'secondary'"
        (buttonClick)="goBack()"
      />
    </div>
  </div>
</div>
